import React, {useState, useEffect} from 'react'
import {FormattedMessage, defineMessage, useIntl} from 'react-intl'
import {useProductList} from '../util/product-list-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useLocation} from 'react-router-dom'
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerFooter,
    Text,
    Flex,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Icons
import {CloseIcon} from '../../../components/custom-icons'

import LoadingSpinner from '../../../components/loading-spinner'
import Refinements from './refinements'
import {useGTM} from '@app/hooks/use-gtm'

const REFINEMENT_DRAWER_LABEL = defineMessage({
    defaultMessage: 'Close Filter',
    id: 'refinements_drawer.close.aria_label'
})

/**
 * A drawer component that displays product refinement options for filtering a product list.
 *
 * The `RefinementsDrawer` component provides an interactive UI element, typically sliding in from the side,
 * that allows users to select various filters (such as categories, price ranges, brands, etc.) to narrow down
 * the displayed products. It is commonly used in e-commerce applications to enhance product discovery and
 * improve user experience on product listing pages.
 *
 * @component
 * @example
 * <RefinementsDrawer
 *   open={isDrawerOpen}
 *   refinements={refinementOptions}
 *   selectedRefinements={selectedFilters}
 *   onClose={handleDrawerClose}
 *   onApply={handleApplyFilters}
 * />
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Controls whether the drawer is visible.
 * @param {Array<Object>} props.refinements - List of available refinement/filter options.
 * @param {Object} props.selectedRefinements - Currently selected refinement values.
 * @param {Function} props.onClose - Callback function invoked when the drawer is closed.
 * @param {Function} props.onApply - Callback function invoked when filters are applied.
 *
 * @returns {JSX.Element} The rendered refinements drawer component.
 */
const RefinementsDrawer = () => {
    const location = useLocation()
    const {formatMessage} = useIntl()
    const {
        isOpenRefinements,
        onCloseRefinements,
        refinementsOpenRef,
        refinementsCloseRef,
        isProductListLoading,
        productSearchResult,
        searchParams
    } = useProductList()
    const {triggerClickFiltersEvent} = useGTM()
    const [localFilters, setLocalFilters] = useState(searchParams.refine || {})
    const [localSorting, setLocalSorting] = useState(
        productSearchResult?.selectedSortingOption
            ? {
                  id: productSearchResult.selectedSortingOption,
                  label: productSearchResult.selectedSortingOption
              }
            : {}
    )
    useEffect(() => {
        // Sync filters from URL params on refresh
        if (searchParams.refine) {
            setLocalFilters(searchParams.refine)
        }

        // Sync sorting from URL
        if (searchParams.sort) {
            const sortValue = searchParams.sort
            const sortLabel =
                productSearchResult?.sortingOptions?.find((o) => o.id === sortValue)?.label ||
                sortValue

            setLocalSorting({id: sortValue, label: sortLabel})
        }
    }, [searchParams, productSearchResult?.sortingOptions])
    const navigate = useNavigation()

    return (
        <Drawer
            isOpen={isOpenRefinements}
            onClose={onCloseRefinements}
            initialFocusRef={refinementsCloseRef}
            finalFocusRef={refinementsOpenRef}
            placement="right"
            size="md"
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader px={{base: 10, lg: 20}} py={24}>
                    {/* Using role="heading" instead of a semantic <h1> tag to avoid SEO impact */}
                    <Text
                        as="div"
                        role="heading"
                        aria-level="1"
                        variant="eyeBrowLarge"
                        textTransform="uppercase"
                        paddingRight={24}
                    >
                        <FormattedMessage
                            defaultMessage="Filter"
                            id="product_list.modal.title.filter"
                        />
                    </Text>
                </DrawerHeader>
                <DrawerCloseButton
                    ref={refinementsCloseRef}
                    aria-label={formatMessage(REFINEMENT_DRAWER_LABEL)}
                >
                    <CloseIcon
                        boxSize="14px"
                        svgTitle={formatMessage(REFINEMENT_DRAWER_LABEL)}
                        svgDescription={formatMessage(REFINEMENT_DRAWER_LABEL)}
                    />
                </DrawerCloseButton>

                <DrawerBody px={[10, 10, 10, 20]} py={[10, 10, 10, 10]}>
                    {isProductListLoading && <LoadingSpinner />}

                    <Refinements
                        isLoading={isProductListLoading}
                        filters={productSearchResult?.refinements}
                        sortingOptions={productSearchResult?.sortingOptions}
                        selectedFilters={localFilters}
                        setSelectedFilters={setLocalFilters}
                        selectedSorting={localSorting}
                        setSelectedSorting={setLocalSorting}
                    />
                </DrawerBody>

                <DrawerFooter display="block" width="full" px={[10, 10, 10, 20]} py={20}>
                    <Flex gap={10}>
                        <Button
                            variant="secondary"
                            flex="1"
                            onClick={() => {
                                setLocalFilters({})
                                setLocalSorting({})
                                const params = new URLSearchParams(location.search)
                                params.delete('refine')
                                params.delete('sort')
                                const query = params.toString()
                                navigate(
                                    query ? `${location.pathname}?${query}` : location.pathname
                                )
                                onCloseRefinements()
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Clear All"
                                id="product_list.modal.button.clear_filters"
                            />
                        </Button>
                        <Button
                            variant="primary"
                            flex="1"
                            onClick={() => {
                                triggerClickFiltersEvent(
                                    {
                                        refinements: productSearchResult?.refinements,
                                        selectedRefinements:
                                            productSearchResult?.selectedRefinements
                                    },
                                    {
                                        sortingOptions: productSearchResult?.sortingOptions,
                                        selectedSortingOption:
                                            productSearchResult?.selectedSortingOption
                                    }
                                )
                                const params = new URLSearchParams(location.search)
                                params.delete('refine')
                                params.delete('sort')

                                Object.keys(localFilters).forEach((attr) => {
                                    const values = Array.isArray(localFilters[attr])
                                        ? localFilters[attr]
                                        : [localFilters[attr]]

                                    const joined = values.join('|')

                                    params.append('refine', `${attr}=${joined}`)
                                })

                                if (localSorting?.id) {
                                    params.append('sort', localSorting.id)
                                }

                                // refresh product list
                                const query = params.toString()
                                navigate(
                                    query ? `${location.pathname}?${query}` : location.pathname
                                )
                                onCloseRefinements()
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Apply"
                                id="product_list.modal.button.apply_items"
                            />
                        </Button>
                    </Flex>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

RefinementsDrawer.propTypes = {}

export default RefinementsDrawer
